import dayjs from "dayjs";
import { postClientLog } from "src/api/clientLogs";
import {
  CLIENT_EVENT_TYPE,
  ClientEventType,
  ClientLogPayload,
  RECOVERY_STATUS,
  RecoveryStatus
} from "src/api/clientLogs/interface";
import { getBrowserType } from "./browserType";
import { getSessionId, getStoreNumber } from "./clientSession";

const MAX_FIELD_LENGTH = 100;
const RECOVERY_PENDING_KEY = "ui-client-log:recovery-pending";

const sentKeys = new Set<string>();
let chunkErrorReported = false;

const clip = (value: string) => value.slice(0, MAX_FIELD_LENGTH);

const send = (
  eventType: ClientEventType,
  recoveryStatus: RecoveryStatus,
  options: {
    expectedVersion?: string;
    dedupeKey?: string;
    storeNumber?: string;
  } = {}
): void => {
  try {
    const { expectedVersion, dedupeKey, storeNumber } = options;
    if (dedupeKey) {
      if (sentKeys.has(dedupeKey)) return;
      sentKeys.add(dedupeKey);
    }

    const payload: ClientLogPayload = {
      storeNumber: clip(storeNumber || getStoreNumber()),
      timestamp: dayjs().toISOString(),
      eventType,
      buildVersion: clip(process.env.VITE_VERSION_NUMBER ?? ""),
      sessionId: getSessionId(),
      browserType: clip(getBrowserType()),
      recoveryStatus,
      ...(expectedVersion ? { expectedVersion: clip(expectedVersion) } : {})
    };

    Promise.resolve(postClientLog(payload)).catch((): void => {});
  } catch {
    /* Logging must never affect the user session. */
  }
};

const markRecoveryPending = (): void => {
  try {
    window.sessionStorage.setItem(RECOVERY_PENDING_KEY, "1");
  } catch {
    /* Without storage the outcome of the reload cannot be linked. */
  }
};

const consumeRecoveryPending = (): boolean => {
  try {
    const pending =
      window.sessionStorage.getItem(RECOVERY_PENDING_KEY) !== null;
    if (pending) window.sessionStorage.removeItem(RECOVERY_PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
};

export const hasRecoveryPending = (): boolean => {
  try {
    return window.sessionStorage.getItem(RECOVERY_PENDING_KEY) !== null;
  } catch {
    return false;
  }
};

export const reportVersionMismatch = (
  expectedVersion: string,
  storeNumber?: string
): void =>
  send(CLIENT_EVENT_TYPE.VERSION_MISMATCH, RECOVERY_STATUS.NOT_ATTEMPTED, {
    expectedVersion,
    storeNumber,
    dedupeKey: `VERSION_MISMATCH|${process.env.VITE_VERSION_NUMBER}|${expectedVersion}`
  });

export const reportChunkLoadError = (storeNumber?: string): void => {
  if (chunkErrorReported) return;
  chunkErrorReported = true;

  const afterAttempt = consumeRecoveryPending();
  send(
    CLIENT_EVENT_TYPE.CHUNK_LOAD_ERROR,
    afterAttempt ? RECOVERY_STATUS.ATTEMPTED : RECOVERY_STATUS.NOT_ATTEMPTED,
    { storeNumber }
  );
  if (afterAttempt) {
    send(CLIENT_EVENT_TYPE.RECOVERY_FAILED, RECOVERY_STATUS.FAILED, {
      storeNumber
    });
  }
};

export const reportAutoReloadAttempt = (storeNumber?: string): void => {
  markRecoveryPending();
  send(CLIENT_EVENT_TYPE.AUTO_RELOAD_ATTEMPT, RECOVERY_STATUS.ATTEMPTED, {
    storeNumber
  });
};

export const confirmRecoverySuccess = (storeNumber?: string): void => {
  if (consumeRecoveryPending()) {
    send(CLIENT_EVENT_TYPE.RECOVERY_SUCCESS, RECOVERY_STATUS.SUCCESS, {
      storeNumber
    });
  }
};

export const reportManualReloadAttempt = (storeNumber?: string): void => {
  markRecoveryPending();
  send(CLIENT_EVENT_TYPE.MANUAL_RELOAD_ATTEMPT, RECOVERY_STATUS.ATTEMPTED, {
    storeNumber
  });
};
